/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ApolloClient, HttpLink, InMemoryCache, ApolloProvider, from, fromPromise, ServerError } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import possibleTypesResult from "~/generated/fragment-matcher";
import { auth0Client } from "~/services/auth";

async function getNewToken() {
  await auth0Client.checkSession({ ignoreCache: true });
  const token = await auth0Client.getIdTokenClaims();

  return token?.__raw;
}

let isRefreshing = false;
const pendingRequests: (() => void)[] = [];

function resolvePendingRequests() {
  pendingRequests.forEach((callback) => callback());
  pendingRequests.length = 0;
}

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors != null) {
    // Async operations don't work inside forEach array method callback
    for (const err of graphQLErrors) {
      if (err?.extensions?.code === "UNAUTHENTICATED") {
        let forwardObservable = null;

        if (!isRefreshing) {
          isRefreshing = true;
          forwardObservable = fromPromise(
            getNewToken()
              .then((token) => {
                // Store the new tokens for your auth link
                resolvePendingRequests();
                return token;
              })
              .catch(async () => {
                pendingRequests.length = 0;
                await auth0Client.logout({
                  returnTo: window.location.origin,
                });
              })
              .finally(() => {
                isRefreshing = false;
              })
          ).filter(Boolean);
        } else {
          // Will only emit once the Promise is resolved
          forwardObservable = fromPromise(
            new Promise((resolve) => {
              pendingRequests.push(() => resolve(undefined));
            })
          );
        }

        return forwardObservable.flatMap(() => forward(operation));
      }
    }
  }
});

function customFetch(uri: string, options: RequestInit) {
  const { operationName = "" } = JSON.parse(options.body?.toString() ?? "{}") as { operationName?: string };

  const fetchUrl = `${uri}?opname=${operationName}`;
  return fetch(fetchUrl, options);
}

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  fetch: customFetch,
});

const authLink = setContext(async (_, { headers }: { headers?: Record<string, string> }) => {
  try {
    // Get saved token or refresh it in case it expired
    await auth0Client.getTokenSilently();
    const token = await auth0Client.getIdTokenClaims();

    return {
      headers: {
        ...headers,
        ...(token != null && { Authorization: token.__raw }),
      },
    };
  } catch {
    window.location.reload();
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error: ServerError, _operation) => {
      // Prevent retries when server is stuck, don't send additional load to handle
      if (error?.statusCode === 503) {
        return false;
      }

      return !!error;
    },
  },
});

export const cache = new InMemoryCache({
  typePolicies: {
    MSPAccountProducts: {
      merge: true,
    },
  },
  ...possibleTypesResult,
});

const client = new ApolloClient({
  cache,
  link: from([authLink, errorLink, retryLink, httpLink]),
  name: "msp",
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
  },
});

export function CustomApolloProvider({ children }: { children: React.ReactNode }) {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
